import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { JsonQuestion, JsonQuestionOption, JsonQuestionnaireSection } from './types';
import type { JsonAnswerValue, QuestionRendererVariant } from './QuestionRenderer';
import { QuestionRenderer } from './QuestionRenderer';
import { isNoneLikeValue, isNoneOption } from './noneOptions';
import { buildQuestionIdToStorageKeyMap, getQuestionKey, mergeQuestionIdToStorageKeyMaps, resolveStorageKeyForQuestionId } from './questionKey';
import { getVisibleOrderedQuestions } from './visibleQuestions';
import {
  buildQuestionnairePrefillScopeKey,
  buildQuestionnairePrefillFromAssignedClient,
  looksLikeMedicalConditionsQuestion,
  resolveEffectiveTriggerContext,
  resolveQuestionnaireDemographicContext,
  type QuestionnaireDemographicContext,
} from './clientContext';
import type { SharedQuestionnaireAssignedClient } from '@/lib/api/questionnaires';
import { getActiveQuestionCount } from './questionMetrics';
import { TriggeredQuestionnaireCard } from './TriggeredQuestionnaireCard';
import {
  defaultTriggerRules,
  evaluateTriggerRules,
  mergeTriggerConfigFromSection,
  selectedValuesFromAnswer,
  useTriggeredQuestionnaires,
  type TriggerConditionContext,
  type TriggerRule,
  type TriggerRuleConfig,
  type TriggeredBlock,
  type TriggeredQuestionnaireMeta,
} from '@/lib/questionnaire-triggers';
import { isTriggerQuestion } from '@/lib/questionnaire-triggers/evaluate';
import {
  addQuestionnaireIds,
  getSectionQuestionnaireIds,
  getTriggeredBlockQuestionnaireIds,
  isPreConsultationScreening,
  normalizeQuestionnaireId,
  setsIntersect,
} from '@/lib/questionnaire-triggers/questionnaireIdentity';

export const INJECTED_PREFIX = 'injected';

function getPopupSectionLabel(question: { label?: string }): string {
  return question.label?.trim() ?? '';
}

function getPopupSectionAccent(label: string | null): string {
  const normalized = label?.trim().toLowerCase() ?? '';
  if (normalized.includes('consent')) return '#2EC4B6';
  if (normalized.includes('demographic')) return '#4B7CFF';
  if (normalized.includes('anthrop')) return '#8B5CF6';
  if (normalized.includes('medical')) return '#F97316';
  if (normalized.includes('goal')) return '#D4A017';
  if (normalized.includes('life')) return '#C29B00';
  if (normalized.includes('diet')) return '#16A34A';
  if (normalized.includes('schedule')) return '#7C3AED';
  return '#25A8DA';
}

export function injectedAnswerKey(sectionId: string, questionKey: string): string {
  return `${INJECTED_PREFIX}:${sectionId}:${questionKey}`;
}

export function getOrderedQuestions(section: JsonQuestionnaireSection) {
  const active = (section.questions ?? []).filter((q) => q.isActive !== false);
  return active.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const OTHER_TEXT_SUFFIX = '_otherText';

function isEmptyAnswer(value: JsonAnswerValue): boolean {
  return value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

function assignedMedicalConditionsSignature(
  client: SharedQuestionnaireAssignedClient | undefined
): string {
  return (client?.medicalConditions ?? [])
    .map((condition) => condition.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join('\u0001');
}

function questionTextBlob(question: JsonQuestion): string {
  return [
    question.questionText,
    question.label,
    question.note,
    question.targetMapping?.field,
    question.externalId,
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
}

function isSafetyNoneFollowUpSelection(question: JsonQuestion, option: JsonQuestionOption): boolean {
  if (!isNoneOption(option)) return false;
  return isSafetyNoneFollowUpQuestion(question);
}

function isSafetyNoneFollowUpQuestion(question: JsonQuestion): boolean {
  if (!question.showWhen?.questionId) return false;
  if (!question.showWhen.optionValues.some((value) => value.trim().toLowerCase() === 'yes')) return false;

  const text = questionTextBlob(question);
  return /\ballerg/.test(text) || /\bintoler/.test(text);
}

function answerHasNoneValue(value: JsonAnswerValue): boolean {
  if (typeof value === 'string') return isNoneLikeValue(value);
  if (Array.isArray(value)) return value.some(isNoneLikeValue);
  return false;
}

function getNoOptionValue(question: JsonQuestion | undefined): string {
  const noOption = question?.options?.find((option) => {
    const normalized = `${option.label} ${option.value}`
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return normalized === 'no no' || normalized.split(' ').includes('no');
  });
  return noOption?.value ?? 'No';
}

function findQuestionByRef(questions: JsonQuestion[] | undefined, refId: string): JsonQuestion | undefined {
  return (questions ?? []).find((question) => {
    const key = getQuestionKey(question);
    return question._id === refId || question.externalId === refId || key === refId;
  });
}

function getOtherTextValue(value: JsonAnswerValue): string | string[] | undefined {
  return typeof value === 'string' || Array.isArray(value) ? value : undefined;
}

function scopedAnswersForInjectedSection(
  section: JsonQuestionnaireSection,
  answers: Record<string, JsonAnswerValue>,
  storageSectionId: string
): Record<string, JsonAnswerValue> {
  const scoped: Record<string, JsonAnswerValue> = {};
  for (const q of section.questions ?? []) {
    const key = getQuestionKey(q);
    const value = answers[injectedAnswerKey(storageSectionId, key)];
    if (value !== undefined) {
      scoped[key] = value;
    }
    const otherVal = answers[injectedAnswerKey(storageSectionId, key + OTHER_TEXT_SUFFIX)];
    if (otherVal !== undefined) {
      scoped[key + OTHER_TEXT_SUFFIX] = otherVal;
    }
  }
  return scoped;
}

function buildTriggeredMeta(
  section: JsonQuestionnaireSection,
  rule: TriggerRule,
  config: TriggerRuleConfig
): TriggeredQuestionnaireMeta {
  const displayTitle = rule.id.startsWith('r-dm-') ? 'Diabetes' : section.title;
  const count = getActiveQuestionCount(section);
  const est =
    section.estimatedTimeMinutes ??
    (config.estimatedTimePerQuestionMinutes ?? 2) * count;
  return {
    questionnaireId: rule.questionnaireId,
    title: displayTitle,
    description: section.description,
    questionCount: count,
    estimatedTimeMinutes: est,
    triggerReason: rule.triggerReason,
  };
}

export interface DynamicQuestionnaireFlowProps {
  section: JsonQuestionnaireSection;
  answers: Record<string, JsonAnswerValue>;
  onAnswerChange: (questionKey: string, value: JsonAnswerValue) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  questionVariant?: QuestionRendererVariant;
  hideTitle?: boolean;
  hideDescription?: boolean;
  /** Suppress question.note info tooltips (fill-manually and client-facing link). */
  hideQuestionNotes?: boolean;
  /** Trigger rule config. Defaults to defaultTriggerRules. */
  triggerConfig?: TriggerRuleConfig;
  triggerContext?: TriggerConditionContext;
  /** Assigned client from share API; used for prefill fallback and demographic filtering. */
  assignedClient?: SharedQuestionnaireAssignedClient;
  /** Fetcher for triggered questionnaires by ID. Required for triggers to work. */
  fetchQuestionnaire?: (id: string) => Promise<JsonQuestionnaireSection>;
  /** Notify parent of all triggered blocks (for validate/buildPayload). Flattened, globally deduped by questionnaireId. */
  onTriggeredChange?: (blocks: TriggeredBlock[]) => void;
  /** Notify parent when an option is selected, without changing core answer handling. */
  onOptionSelect?: (question: JsonQuestion, option: JsonQuestionOption) => void;
  /** Allows a parent flow to disable individual questions without locking the entire questionnaire. */
  isQuestionDisabled?: (question: JsonQuestion, questionKey: string) => boolean;
}

/**
 * DynamicQuestionnaireFlow renders the base section questions and conditionally
 * injected questionnaires. Selected conditions (and their types) come from answers;
 * triggered questionnaires update live as selections change.
 */
export function DynamicQuestionnaireFlow({
  section,
  answers,
  onAnswerChange,
  errors,
  disabled = false,
  questionVariant = 'default',
  hideTitle = false,
  hideDescription = false,
  hideQuestionNotes = false,
  triggerConfig = defaultTriggerRules,
  triggerContext,
  assignedClient,
  fetchQuestionnaire,
  onTriggeredChange,
  onOptionSelect,
  isQuestionDisabled,
}: DynamicQuestionnaireFlowProps) {
  const effectiveSection = section;
  const config = useMemo(
    () => mergeTriggerConfigFromSection(effectiveSection, triggerConfig),
    [effectiveSection, triggerConfig]
  );
  const demographicContext = useMemo<QuestionnaireDemographicContext>(
    () => resolveQuestionnaireDemographicContext(answers, assignedClient, effectiveSection, config.contextFields),
    [answers, assignedClient, effectiveSection, config.contextFields]
  );
  const fetchFn = useCallback(
    (id: string) => {
      if (!fetchQuestionnaire) throw new Error('fetchQuestionnaire is required for triggered questionnaires');
      return fetchQuestionnaire(id);
    },
    [fetchQuestionnaire]
  );

  const { triggeredByQuestion, isLoading, error } = useTriggeredQuestionnaires({
    section: fetchQuestionnaire ? effectiveSection : null,
    answers,
    config,
    fetchQuestionnaire: fetchFn,
    triggerContext,
    demographicContext,
    assignedClient,
  });

  const questions = useMemo(
    () => getVisibleOrderedQuestions(effectiveSection, answers, demographicContext),
    [effectiveSection, answers, demographicContext]
  );
  const assignedPrefill = useMemo(
    () =>
      assignedClient
        ? buildQuestionnairePrefillFromAssignedClient(
            effectiveSection,
            assignedClient,
            config.contextFields,
            config
          )
        : {},
    [assignedClient, effectiveSection, config]
  );
  const assignedPrefillScopeKey = useMemo(
    () => buildQuestionnairePrefillScopeKey(effectiveSection.id, assignedPrefill),
    [assignedPrefill, effectiveSection.id]
  );
  const assignedMedicalSignature = useMemo(
    () => assignedMedicalConditionsSignature(assignedClient),
    [assignedClient]
  );
  const appliedAssignedPrefillKeysRef = useRef<Set<string>>(new Set());
  const previousAssignedMedicalSignatureRef = useRef<string | null>(null);
  const context = useMemo(
    () =>
      resolveEffectiveTriggerContext(
        answers as Record<string, unknown>,
        assignedClient,
        effectiveSection,
        triggerContext,
        config.contextFields
      ),
    [answers, assignedClient, effectiveSection, triggerContext, config.contextFields]
  );

  const triggerQuestionKeySet = useMemo(() => {
    const s = new Set<string>();
    for (const q of questions) {
      if (isTriggerQuestion(q, config)) s.add(getQuestionKey(q));
    }
    return s;
  }, [questions, config]);

  type TriggeredEntry = { sourceQuestionKey: string; block: TriggeredBlock };

  const currentTriggeredEntries = useMemo(() => {
    const seen = new Set<string>();
    const m = new Map<string, TriggeredEntry>();
    for (const q of questions) {
      const key = getQuestionKey(q);
      if (!triggerQuestionKeySet.has(key)) continue;
      const blocks = triggeredByQuestion.get(key) ?? [];
      for (const b of blocks) {
        const storageId = b.meta.questionnaireId;
        if (seen.has(storageId)) continue;
        seen.add(storageId);
        m.set(storageId, { sourceQuestionKey: key, block: b });
      }
    }
    return m;
  }, [questions, triggerQuestionKeySet, triggeredByQuestion]);

  const blocksPerQuestion = useMemo(() => {
    const m = new Map<string, TriggeredBlock[]>();
    for (const entry of currentTriggeredEntries.values()) {
      if (!triggerQuestionKeySet.has(entry.sourceQuestionKey)) continue;
      const list = m.get(entry.sourceQuestionKey) ?? [];
      list.push(entry.block);
      m.set(entry.sourceQuestionKey, list);
    }
    return m;
  }, [currentTriggeredEntries, triggerQuestionKeySet]);

  // Unique questionnaires currently triggered from current answers.
  const selectedTriggeredQuestionnaires = useMemo(() => {
    return Array.from(currentTriggeredEntries.values(), (entry) => entry.block);
  }, [currentTriggeredEntries]);

  const nestedRulesBySourceQuestion = useMemo(() => {
    const bySource = new Map<string, TriggerRule[]>();
    const activeQuestionnaireIds = getSectionQuestionnaireIds(effectiveSection);
    for (const entry of currentTriggeredEntries.values()) {
      addQuestionnaireIds(activeQuestionnaireIds, getTriggeredBlockQuestionnaireIds(entry.block));
    }

    for (const entry of currentTriggeredEntries.values()) {
      const sourceSection = entry.block.section;
      if (isPreConsultationScreening(sourceSection)) continue;
      const sourceStorageId = entry.block.meta.questionnaireId;
      const sourceAnswers = scopedAnswersForInjectedSection(sourceSection, answers, sourceStorageId);
      const sourceQuestions = getVisibleOrderedQuestions(sourceSection, sourceAnswers, demographicContext);
      const selfQuestionnaireIds = getTriggeredBlockQuestionnaireIds(entry.block);

      for (const q of sourceQuestions) {
        if (!isTriggerQuestion(q, config)) continue;
        const key = getQuestionKey(q);
        const selected = selectedValuesFromAnswer(sourceAnswers[key]);
        const sourceKey = `${sourceStorageId}:${key}`;
        const existing = bySource.get(sourceKey) ?? [];
        const combined = existing.slice();
        const matched = evaluateTriggerRules(selected, context, config);

        for (const rule of matched) {
          const ruleQuestionnaireId = normalizeQuestionnaireId(rule.questionnaireId);
          if (!ruleQuestionnaireId) continue;
          if (selfQuestionnaireIds.has(ruleQuestionnaireId)) continue;
          if (activeQuestionnaireIds.has(ruleQuestionnaireId)) continue;
          if (combined.some((r) => r.questionnaireId === rule.questionnaireId)) continue;

          combined.push(rule);
          activeQuestionnaireIds.add(ruleQuestionnaireId);
        }

        if (combined.length > 0) bySource.set(sourceKey, combined);
      }
    }

    return bySource;
  }, [currentTriggeredEntries, answers, config, context, effectiveSection, demographicContext]);

  const nestedQuestionnaireIds = useMemo(() => {
    const ids = new Set<string>();
    for (const rules of nestedRulesBySourceQuestion.values()) {
      for (const rule of rules) {
        const id = normalizeQuestionnaireId(rule.questionnaireId);
        if (id) ids.add(id);
      }
    }
    return Array.from(ids);
  }, [nestedRulesBySourceQuestion]);

  const triggerContextCacheKey = `${context.gender ?? ''}:${context.age ?? ''}:${context.pregnant ?? ''}`;

  const nestedQueryConfigs = useMemo(() => {
    const list = nestedQuestionnaireIds.map((id) => ({
      queryKey: ['triggeredQuestionnaire', 'nested', id, triggerContextCacheKey] as const,
      queryFn: () => fetchFn(id),
      enabled: Boolean(fetchQuestionnaire),
      staleTime: 0,
    }));
    if (list.length === 0) {
      list.push({
        queryKey: ['triggeredQuestionnaire', 'nested', '__placeholder', triggerContextCacheKey] as const,
        queryFn: () => Promise.resolve(null as unknown as JsonQuestionnaireSection),
        enabled: false,
        staleTime: 0,
      });
    }
    return list;
  }, [nestedQuestionnaireIds, fetchFn, fetchQuestionnaire, triggerContextCacheKey]);

  const nestedQueries = useQueries({ queries: nestedQueryConfigs });

  const nestedSectionsById = useMemo(() => {
    const m = new Map<string, JsonQuestionnaireSection>();
    for (let i = 0; i < nestedQuestionnaireIds.length; i++) {
      const data = nestedQueries[i]?.data;
      if (data) m.set(nestedQuestionnaireIds[i], data);
    }
    return m;
  }, [nestedQuestionnaireIds, nestedQueries]);

  const nestedBlocksBySourceQuestion = useMemo(() => {
    const m = new Map<string, TriggeredBlock[]>();
    const seenQuestionnaireIds = getSectionQuestionnaireIds(effectiveSection);
    for (const entry of currentTriggeredEntries.values()) {
      addQuestionnaireIds(seenQuestionnaireIds, getTriggeredBlockQuestionnaireIds(entry.block));
    }
    for (const [sourceKey, rules] of nestedRulesBySourceQuestion.entries()) {
      const blocks: TriggeredBlock[] = [];
      for (const rule of rules) {
        const nestedSection = nestedSectionsById.get(rule.questionnaireId);
        if (!nestedSection) continue;
        const sourceStorageId = sourceKey.split(':')[0];
        const sourceBlock = currentTriggeredEntries.get(sourceStorageId)?.block;
        const nestedQuestionnaireIds = getSectionQuestionnaireIds(nestedSection);
        const ruleQuestionnaireId = normalizeQuestionnaireId(rule.questionnaireId);
        if (ruleQuestionnaireId) nestedQuestionnaireIds.add(ruleQuestionnaireId);
        if (sourceBlock && setsIntersect(nestedQuestionnaireIds, getTriggeredBlockQuestionnaireIds(sourceBlock))) continue;
        if (setsIntersect(nestedQuestionnaireIds, seenQuestionnaireIds)) continue;
        addQuestionnaireIds(seenQuestionnaireIds, nestedQuestionnaireIds);
        blocks.push({
          section: nestedSection,
          meta: buildTriggeredMeta(nestedSection, rule, config),
        });
      }
      if (blocks.length > 0) m.set(sourceKey, blocks);
    }
    return m;
  }, [nestedRulesBySourceQuestion, nestedSectionsById, config, currentTriggeredEntries, effectiveSection]);

  const allTriggeredQuestionnaires = useMemo(() => {
    const out: TriggeredBlock[] = [];
    const seen = new Set<string>();
    for (const block of selectedTriggeredQuestionnaires) {
      const id = block.meta.questionnaireId;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(block);
    }
    for (const blocks of nestedBlocksBySourceQuestion.values()) {
      for (const block of blocks) {
        const id = block.meta.questionnaireId;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(block);
      }
    }
    return out;
  }, [selectedTriggeredQuestionnaires, nestedBlocksBySourceQuestion]);

  const idToKeyMap = useMemo(() => {
    const map = buildQuestionIdToStorageKeyMap(effectiveSection.questions);
    for (const block of allTriggeredQuestionnaires) {
      mergeQuestionIdToStorageKeyMaps(map, block.section.questions);
    }
    return map;
  }, [effectiveSection.questions, allTriggeredQuestionnaires]);

  const resolveAnswer = useCallback(
    (id: string, localAnswers?: Record<string, JsonAnswerValue>) => {
      const resolvedKey = resolveStorageKeyForQuestionId(idToKeyMap, id);
      if (localAnswers && localAnswers[resolvedKey] !== undefined) {
        return localAnswers[resolvedKey];
      }
      return answers[resolvedKey];
    },
    [idToKeyMap, answers]
  );

  useEffect(() => {
    onTriggeredChange?.(allTriggeredQuestionnaires);
  }, [allTriggeredQuestionnaires, onTriggeredChange]);

  useEffect(() => {
    appliedAssignedPrefillKeysRef.current.clear();
  }, [assignedPrefillScopeKey]);

  useEffect(() => {
    if (!assignedClient || disabled) return;
    const appliedKeys = appliedAssignedPrefillKeysRef.current;

    for (const question of questions) {
      const key = getQuestionKey(question);
      const prefillValue = assignedPrefill[key];
      if (prefillValue === undefined || appliedKeys.has(key)) continue;

      if (isEmptyAnswer(answers[key])) {
        appliedKeys.add(key);
        onAnswerChange(key, prefillValue);
      } else {
        appliedKeys.add(key);
      }
    }
  }, [answers, assignedClient, assignedPrefill, disabled, onAnswerChange, questions]);

  useEffect(() => {
    if (!assignedClient || disabled) return;

    const previousSignature = previousAssignedMedicalSignatureRef.current;
    previousAssignedMedicalSignatureRef.current = assignedMedicalSignature;
    if (previousSignature === null || previousSignature === assignedMedicalSignature || assignedMedicalSignature) {
      return;
    }

    for (const question of questions) {
      if (!looksLikeMedicalConditionsQuestion(question)) continue;

      const key = getQuestionKey(question);
      if (!isEmptyAnswer(answers[key])) {
        onAnswerChange(key, undefined as unknown as JsonAnswerValue);
      }

      const otherKey = `${key}${OTHER_TEXT_SUFFIX}`;
      if (!isEmptyAnswer(answers[otherKey])) {
        onAnswerChange(otherKey, undefined as unknown as JsonAnswerValue);
      }
    }
  }, [answers, assignedClient, assignedMedicalSignature, disabled, onAnswerChange, questions]);

  const handleOptionSelect = useCallback(
    (
      question: JsonQuestion,
      option: JsonQuestionOption,
      scopeQuestions: JsonQuestion[] = effectiveSection.questions,
      storageId?: string
    ) => {
      onOptionSelect?.(question, option);

      if (!isSafetyNoneFollowUpSelection(question, option)) return;
      const parentRef = question.showWhen?.questionId;
      if (!parentRef) return;

      const parentQuestion = findQuestionByRef(scopeQuestions, parentRef) ?? findQuestionByRef(effectiveSection.questions, parentRef);
      const parentKey = resolveStorageKeyForQuestionId(idToKeyMap, parentRef);
      const parentIsInScope = Boolean(findQuestionByRef(scopeQuestions, parentRef));
      const answerKey = storageId && parentIsInScope ? injectedAnswerKey(storageId, parentKey) : parentKey;
      onAnswerChange(answerKey, getNoOptionValue(parentQuestion));
    },
    [effectiveSection.questions, idToKeyMap, onAnswerChange, onOptionSelect]
  );

  useEffect(() => {
    const syncScope = (scopeQuestions: JsonQuestion[], storageId?: string) => {
      for (const question of scopeQuestions) {
        if (!isSafetyNoneFollowUpQuestion(question)) continue;

        const questionKey = getQuestionKey(question);
        const answerKey = storageId ? injectedAnswerKey(storageId, questionKey) : questionKey;
        if (!answerHasNoneValue(answers[answerKey])) continue;

        const parentRef = question.showWhen?.questionId;
        if (!parentRef) continue;

        const parentQuestion = findQuestionByRef(scopeQuestions, parentRef) ?? findQuestionByRef(effectiveSection.questions, parentRef);
        const parentKey = resolveStorageKeyForQuestionId(idToKeyMap, parentRef);
        const parentIsInScope = Boolean(findQuestionByRef(scopeQuestions, parentRef));
        const parentAnswerKey = storageId && parentIsInScope ? injectedAnswerKey(storageId, parentKey) : parentKey;
        const noValue = getNoOptionValue(parentQuestion);
        if (answers[parentAnswerKey] !== noValue) {
          onAnswerChange(parentAnswerKey, noValue);
        }
      }
    };

    syncScope(effectiveSection.questions);
    for (const block of allTriggeredQuestionnaires) {
      syncScope(block.section.questions ?? [], block.meta.questionnaireId);
    }
  }, [allTriggeredQuestionnaires, answers, effectiveSection.questions, idToKeyMap, onAnswerChange]);

  // Drop injected answers for conditional questionnaires that are no longer active (e.g. female → male).
  useEffect(() => {
    const activeStorageIds = new Set(
      allTriggeredQuestionnaires.map((block) => block.meta.questionnaireId)
    );
    const prefix = `${INJECTED_PREFIX}:`;
    for (const key of Object.keys(answers)) {
      if (!key.startsWith(prefix)) continue;
      const storageId = key.slice(prefix.length).split(':')[0];
      if (!storageId || activeStorageIds.has(storageId)) continue;
      onAnswerChange(key, undefined as unknown as JsonAnswerValue);
    }
  }, [allTriggeredQuestionnaires, answers, onAnswerChange]);

  return (
    <div className="space-y-8">
      {hideTitle ? null : (
        <div className="space-y-2 pb-2 border-b border-border/60">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{section.title}</h1>
          {hideDescription ? null : section.description ? (
            <p className="text-muted-foreground leading-relaxed text-[15px]">{section.description}</p>
          ) : null}
        </div>
      )}

      {/* Base section questions with inline triggered blocks */}
      <div className="space-y-6">
        {questions.map((q, questionIndex) => {
          const key = getQuestionKey(q);
          const listKey = `question-${key || q._id || 'q'}-${questionIndex}`;
          const triggeredBlocks = blocksPerQuestion.get(key) ?? [];
          const popupSectionLabel = questionVariant === 'popup' ? getPopupSectionLabel(q) : null;
          const previousPopupSectionLabel =
            questionVariant === 'popup' && questionIndex > 0
              ? getPopupSectionLabel(questions[questionIndex - 1])
              : null;
          const showPopupSectionHeader =
            questionVariant === 'popup' &&
            Boolean(popupSectionLabel) &&
            (questionIndex === 0 || popupSectionLabel !== previousPopupSectionLabel);
          const popupSectionAccent = getPopupSectionAccent(popupSectionLabel);
          const questionDisabled = disabled || Boolean(isQuestionDisabled?.(q, key));
          return (
            <div key={listKey} className="space-y-6">
              {showPopupSectionHeader ? (
                <div className="mb-2 border-b border-[#E6EBF2] pb-3">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-7 w-[6px] rounded-full"
                      style={{ backgroundColor: popupSectionAccent }}
                    />
                    <p
                      className="text-[15px] font-semibold tracking-[0.08em]"
                      style={{ color: popupSectionAccent, textTransform: 'uppercase' }}
                    >
                      {popupSectionLabel}
                    </p>
                  </div>
                </div>
              ) : null}
              <QuestionRenderer
                question={q}
                value={answers[key]}
                onChange={onAnswerChange}
                otherTextValue={getOtherTextValue(answers[key + OTHER_TEXT_SUFFIX])}
                error={errors?.[key]}
                disabled={questionDisabled}
                variant={questionVariant}
                hideQuestionNote={hideQuestionNotes}
                getAnswer={(id) => resolveAnswer(id)}
                demographicContext={demographicContext}
                onOptionSelect={(question, option) =>
                  handleOptionSelect(question, option, effectiveSection.questions)
                }
              />
              {triggeredBlocks.length > 0 ? (
                <div className="space-y-8 pt-2">
                  {triggeredBlocks.map((block) => {
                    const { section: injSection, meta } = block;
                    const storageId = meta.questionnaireId;
                    const injectedAnswers = scopedAnswersForInjectedSection(injSection, answers, storageId);
                    const injectedQuestions = getVisibleOrderedQuestions(
                      injSection,
                      injectedAnswers,
                      demographicContext
                    );
                    return (
                      <div key={`triggered-${storageId}`} className="space-y-6">
                        <TriggeredQuestionnaireCard meta={meta}>
                          {injectedQuestions.map((iq, iqIndex) => {
                            const iqKey = getQuestionKey(iq);
                            const ansKey = injectedAnswerKey(storageId, iqKey);
                            const questionDisabled = disabled || Boolean(isQuestionDisabled?.(iq, ansKey));
                            const itemKey = `triggered-${storageId}-q-${iqKey}-${iqIndex}`;
                            const nestedSourceKey = `${storageId}:${iqKey}`;
                            const nestedBlocks = nestedBlocksBySourceQuestion.get(nestedSourceKey) ?? [];
                            return (
                              <div key={itemKey} className="space-y-6">
                                <QuestionRenderer
                                  question={iq}
                                  value={injectedAnswers[iqKey]}
                                  onChange={(k, value) => onAnswerChange(injectedAnswerKey(storageId, k), value)}
                                  otherTextValue={getOtherTextValue(injectedAnswers[iqKey + OTHER_TEXT_SUFFIX])}
                                  error={errors?.[ansKey]}
                                  disabled={questionDisabled}
                                  variant={questionVariant}
                                  hideQuestionNote={hideQuestionNotes}
                                  getAnswer={(id) => resolveAnswer(id, injectedAnswers)}
                                  demographicContext={demographicContext}
                                  onOptionSelect={(question, option) =>
                                    handleOptionSelect(question, option, injSection.questions, storageId)
                                  }
                                />

                                {nestedBlocks.length > 0 ? (
                                  <div className="space-y-8 pt-2">
                                    {nestedBlocks.map((nestedBlock) => {
                                      const nestedSection = nestedBlock.section;
                                      const nestedStorageId = nestedBlock.meta.questionnaireId;
                                      const nestedAnswers = scopedAnswersForInjectedSection(
                                        nestedSection,
                                        answers,
                                        nestedStorageId
                                      );
                                      const nestedQuestions = getVisibleOrderedQuestions(
                                        nestedSection,
                                        nestedAnswers,
                                        demographicContext
                                      );
                                      return (
                                        <div key={`triggered-${storageId}-${nestedStorageId}`} className="space-y-6">
                                          <TriggeredQuestionnaireCard meta={nestedBlock.meta}>
                                            {nestedQuestions.map((nq, nqIndex) => {
                                              const nqKey = getQuestionKey(nq);
                                              const nestedAnsKey = injectedAnswerKey(nestedStorageId, nqKey);
                                              const questionDisabled =
                                                disabled || Boolean(isQuestionDisabled?.(nq, nestedAnsKey));
                                              return (
                                                <QuestionRenderer
                                                  key={`triggered-${nestedStorageId}-q-${nqKey}-${nqIndex}`}
                                                  question={nq}
                                                  value={nestedAnswers[nqKey]}
                                                  onChange={(k, value) =>
                                                    onAnswerChange(injectedAnswerKey(nestedStorageId, k), value)
                                                  }
                                                  otherTextValue={getOtherTextValue(nestedAnswers[nqKey + OTHER_TEXT_SUFFIX])}
                                                  error={errors?.[nestedAnsKey]}
                                                  disabled={questionDisabled}
                                                  variant={questionVariant}
                                                  hideQuestionNote={hideQuestionNotes}
                                                  getAnswer={(id) => {
                                                    const resolvedKey = resolveStorageKeyForQuestionId(idToKeyMap, id);
                                                    if (nestedAnswers[resolvedKey] !== undefined) return nestedAnswers[resolvedKey];
                                                    if (injectedAnswers[resolvedKey] !== undefined) return injectedAnswers[resolvedKey];
                                                    return answers[resolvedKey];
                                                  }}
                                                  demographicContext={demographicContext}
                                                  onOptionSelect={(question, option) =>
                                                    handleOptionSelect(question, option, nestedSection.questions, nestedStorageId)
                                                  }
                                                />
                                              );
                                            })}
                                          </TriggeredQuestionnaireCard>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </TriggeredQuestionnaireCard>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground py-2">Loading additional questionnaires…</p>
      ) : null}
      {error ? (
        <p className="text-sm text-destructive py-2" role="alert">{error.message}</p>
      ) : null}
    </div>
  );
}
